import React from 'react';
import { KeyRound } from 'lucide-react';
import {
  ACTION_LABELS,
  FIELD_LABELS,
  baselineOf,
  formatItem,
  itemKey,
  operationChanges,
  operationTitle,
  parseOperations,
} from './governance';
import { reservedDomainLabel } from './reservedDomains';

const BADGES = {
  edit: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  create: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  delete: 'bg-red-50 text-red-700 border-red-100',
};

export const ActionBadge = ({ action }) => {
  const key = ACTION_LABELS[action] ? action : 'edit';
  return (
    <span className={`shrink-0 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${BADGES[key]}`}>
      {ACTION_LABELS[key]}
    </span>
  );
};

const EffectMark = ({ change, item }) => (change.kind === 'domains' && item.effect
  ? <KeyRound size={11} className="inline mr-1 -mt-0.5 text-violet-500" aria-label="Domaine à effet" />
  : null);

// Ajouts et retraits seuls (vue compacte : votes, historique).
const ItemsDiff = ({ change }) => (
  <ul className="flex flex-wrap gap-1.5">
    {change.added.map((item) => (
      <li key={`add-${itemKey(change, item)}`} className="rounded-md border border-emerald-100 bg-emerald-50 px-1.5 py-0.5 text-emerald-800">
        + <EffectMark change={change} item={item} />{formatItem(change, item)}
      </li>
    ))}
    {change.removed.map((item) => (
      <li key={`del-${itemKey(change, item)}`} className="rounded-md border border-red-100 bg-red-50 px-1.5 py-0.5 text-red-700 line-through decoration-red-300">
        <EffectMark change={change} item={item} />{formatItem(change, item)}
      </li>
    ))}
  </ul>
);

// Liste complète après le changement (vue de réunion) : ce qui reste, ce qui s'ajoute, ce qui part, à sa place.
const ItemsFull = ({ change }) => {
  const removed = new Set(change.removed.map((item) => itemKey(change, item)));
  const added = new Set(change.added.map((item) => itemKey(change, item)));
  const rows = [
    ...change.before.map((item) => ({ item, state: removed.has(itemKey(change, item)) ? 'removed' : 'kept' })),
    ...change.after.filter((item) => added.has(itemKey(change, item))).map((item) => ({ item, state: 'added' })),
  ];
  return (
    <ul className="space-y-1">
      {rows.map(({ item, state }) => (
        <li
          key={`${state}-${itemKey(change, item)}`}
          className={`flex items-start gap-2 rounded-md px-2 py-1 text-sm ${
            state === 'added'
              ? 'bg-emerald-50 text-emerald-900 border border-emerald-100'
              : state === 'removed'
                ? 'bg-red-50/70 text-red-700 line-through decoration-red-300 border border-red-100'
                : 'text-slate-700'
          }`}
        >
          <span className={`w-4 shrink-0 text-center font-bold ${state === 'added' ? 'text-emerald-600' : state === 'removed' ? 'text-red-500' : 'text-slate-300'}`}>
            {state === 'added' ? '+' : state === 'removed' ? '−' : '•'}
          </span>
          <span className="min-w-0"><EffectMark change={change} item={item} />{formatItem(change, item)}</span>
        </li>
      ))}
    </ul>
  );
};

const ChangeRow = ({ change, isCreate, readable }) => (
  <div className={readable ? 'px-4 py-3' : 'px-3 py-2'}>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{change.label}</p>
    {change.kind === 'text' ? (
      isCreate || !change.before ? (
        <p className={`text-slate-900 font-medium whitespace-pre-line ${readable ? 'text-sm mt-0.5' : ''}`}>{change.after || '—'}</p>
      ) : (
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-3 ${readable ? 'text-sm mt-0.5' : ''}`}>
          <p className="text-slate-500 whitespace-pre-line"><span className="font-semibold">Avant :</span> {change.before || '—'}</p>
          <p className="text-slate-900 font-medium whitespace-pre-line"><span className="font-semibold">Après :</span> {change.after || '—'}</p>
        </div>
      )
    ) : readable ? (
      <div className="mt-1"><ItemsFull change={change} /></div>
    ) : isCreate ? (
      <p className="text-slate-900">{change.after.map((item) => formatItem(change, item)).join(' · ')}</p>
    ) : (
      <ItemsDiff change={change} />
    )}
  </div>
);

// Un changement proposé : verbe, élément visé (jamais le cercle concerné à sa place) et avant/après.
export const OperationDiff = ({ op, circles = [], index, total, nameOverride, readable = false }) => {
  const title = operationTitle(op, circles);
  const name = nameOverride || title.name;
  const changes = operationChanges(op, circles);
  const isCreate = title.action === 'create';
  const baseline = title.action === 'delete' ? baselineOf(op, circles) : null;
  return (
    <div className={`rounded-xl border border-slate-200 bg-white overflow-hidden ${readable ? 'text-sm shadow-sm' : 'text-xs'}`}>
      <div className={`flex flex-wrap items-center gap-2 bg-slate-50 border-b border-slate-100 ${readable ? 'px-4 py-2.5' : 'px-3 py-2'}`}>
        {total > 1 && <span className="text-[11px] font-semibold text-slate-400">{index + 1}/{total}</span>}
        <ActionBadge action={title.action} />
        <span className={`font-semibold text-slate-900 ${readable ? 'text-base' : 'text-sm'}`}>{name}</span>
        {isCreate && title.where && <span className="text-slate-500">dans « {title.where} »</span>}
      </div>
      {title.action === 'delete' ? (
        <div className="px-3 py-2 text-red-700 space-y-1">
          <p>« {name} » sera supprimé. Ses éventuels sous-rôles remontent dans le cercle parent.</p>
          {baseline?.purpose && (
            <p className="text-slate-500"><span className="font-semibold">{FIELD_LABELS.purpose} actuelle :</span> {baseline.purpose}</p>
          )}
          {Array.isArray(baseline?.reserved_domains) && baseline.reserved_domains.length > 0 && (
            <p className="text-slate-500">
              <span className="font-semibold">{FIELD_LABELS.reserved_domains} perdus :</span>{' '}
              {baseline.reserved_domains.map(reservedDomainLabel).join(', ')}
            </p>
          )}
        </div>
      ) : changes.length === 0 ? (
        <p className="px-3 py-2 text-slate-500 italic">
          {isCreate ? 'Rôle encore vide : donne-lui au moins un nom.' : "Aucun changement de contenu pour l'instant."}
        </p>
      ) : (
        <div className="divide-y divide-slate-100">
          {changes.map((change) => (
            <ChangeRow key={change.key} change={change} isCreate={isCreate} readable={readable} />
          ))}
        </div>
      )}
    </div>
  );
};

export const ProposalDiff = ({
  original,
  proposed,
  action,
  circleLabel,
  operations = [],
  circles = [],
  heading = 'Changements proposés',
  readable = false,
}) => {
  const ops = parseOperations(operations);
  if (ops.length > 0) {
    return (
      <div className="space-y-2 mt-2">
        {heading && <p className="text-xs uppercase text-slate-500 font-semibold">{heading}</p>}
        {ops.map((op, idx) => (
          <OperationDiff
            key={`${op.action || 'edit'}-${op.target_circle_id ?? 'new'}-${idx}`}
            op={op}
            circles={circles}
            index={idx}
            total={ops.length}
            readable={readable}
          />
        ))}
      </div>
    );
  }
  if (!proposed) return null;
  // Ancien format : un seul changement décrit par proposed_data.
  const legacyAction = action || (proposed.delete ? 'delete' : 'edit');
  const legacyOp = {
    action: legacyAction,
    target_circle_id: null,
    data: legacyAction === 'delete' ? {} : proposed,
    original_data: legacyAction === 'create' ? {} : (original || {}),
  };
  return (
    <div className="space-y-2 mt-2">
      {heading && <p className="text-xs uppercase text-slate-500 font-semibold">{heading}</p>}
      <OperationDiff
        op={legacyOp}
        circles={circles}
        index={0}
        total={1}
        nameOverride={legacyAction === 'create' ? (proposed.name || 'Nouveau rôle') : (circleLabel || original?.name || 'Rôle')}
      />
    </div>
  );
};

// Détail d'un vote : un bloc par changement, avec le bon élément visé et son avant/après.
export const VoteDetails = ({ vote, circles = [] }) => {
  if (!vote) return null;
  return (
    <ProposalDiff
      operations={vote.operations || []}
      circles={circles}
      proposed={vote.proposed_data || null}
      action={vote.tension_action}
      circleLabel={vote.tension_title}
    />
  );
};

export default ProposalDiff;
